import { fmtFecha } from '../../lib/utils';
import { useState, useEffect, useMemo } from 'react';
import { comprasApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RefreshCw, DollarSign, Printer, Eye, X, FileText, FileSpreadsheet, Truck, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown, Search } from 'lucide-react';
import { OCNumber } from '../../lib/ocFormat';
import { exportToCSV, printTable } from '../../lib/printUtils';
import { DocRefLink } from '../../components/DocRefLink';

export const ResumenComprasOTs = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [searchOT, setSearchOT] = useState('');
  
  // Modal detalle OT
  const [selectedOT, setSelectedOT] = useState(null);
  const [detalleOT, setDetalleOT] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  // Iter 293.f (Carlos, feb-2026): sort + search en el modal Detalle Costo Materiales.
  const [ocSortBy, setOcSortBy] = useState('numero_oc'); // 'numero_oc' | 'proveedor' | 'fecha' | 'total_neto'
  const [ocSortDir, setOcSortDir] = useState('asc');     // 'asc' | 'desc'
  const [ocSearch, setOcSearch] = useState('');
  
  // Modal detalle OC
  const [selectedOC, setSelectedOC] = useState(null);
  const [detalleOC, setDetalleOC] = useState(null);
  const [loadingDetalleOC, setLoadingDetalleOC] = useState(false);
  // Iter 293.g (Carlos, feb-2026): sort en la tabla de items del modal Detalle OC.
  const [itemSortBy, setItemSortBy] = useState('linea'); // 'linea' | 'descripcion' | 'cantidad' | 'precio' | 'total'
  const [itemSortDir, setItemSortDir] = useState('asc');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await comprasApi.getResumenOTsEjecucion();
      setData(res.data);
    } catch (err) {
      setError('Error al cargar los datos');

    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalle = async (otNumero) => {
    setLoadingDetalle(true);
    setSelectedOT(otNumero);
    // Iter 293.f: reset sort/search al abrir un nuevo detalle
    setOcSearch('');
    setOcSortBy('numero_oc');
    setOcSortDir('asc');
    try {
      const res = await comprasApi.getCostoMaterialesOT(otNumero);
      setDetalleOT(res.data);
    } catch (err) {

      setDetalleOT(null);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const closeDetalle = () => {
    setSelectedOT(null);
    setDetalleOT(null);
    setSelectedOC(null);
    setDetalleOC(null);
  };

  const handleVerDetalleOC = async (numeroOc) => {
    setLoadingDetalleOC(true);
    setSelectedOC(numeroOc);
    // Iter 293.g: reset del sort al abrir otra OC
    setItemSortBy('linea');
    setItemSortDir('asc');
    try {
      const res = await comprasApi.getOrdenCompraDetalle(numeroOc);
      setDetalleOC(res.data);
    } catch (err) {

      setDetalleOC(null);
    } finally {
      setLoadingDetalleOC(false);
    }
  };

  const closeDetalleOC = () => {
    setSelectedOC(null);
    setDetalleOC(null);
  };

  // Iter 293.f (Carlos, feb-2026): sort + búsqueda del listado de OCs del modal.
  const toggleOcSort = (col) => {
    if (ocSortBy === col) {
      setOcSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setOcSortBy(col);
      // default: alfabetos → asc, numéricos/fecha → desc (recientes/altos primero)
      setOcSortDir(col === 'proveedor' ? 'asc' : 'desc');
    }
  };

  const parseFechaISO = (s) => {
    if (!s) return 0;
    // Soporta ISO YYYY-MM-DD y "MM/DD/YY 0" legacy (idem fmtFecha del util)
    const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (isoMatch) return new Date(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`).getTime();
    const mdyMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(s);
    if (mdyMatch) {
      const [, m, d, y] = mdyMatch;
      const yyyy = y.length === 2 ? `20${y}` : y;
      return new Date(`${yyyy}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`).getTime();
    }
    return 0;
  };

  const ocsFiltradasOrdenadas = useMemo(() => {
    const rows = detalleOT?.detalle_ocs || [];
    const q = ocSearch.trim().toLowerCase();
    const filtered = q
      ? rows.filter((oc) =>
          String(oc.numero_oc || '').toLowerCase().includes(q) ||
          String(oc.proveedor || '').toLowerCase().includes(q))
      : rows;
    const dir = ocSortDir === 'asc' ? 1 : -1;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av; let bv;
      switch (ocSortBy) {
        case 'proveedor':
          av = String(a.proveedor || '').toLowerCase();
          bv = String(b.proveedor || '').toLowerCase();
          return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
        case 'fecha':
          av = parseFechaISO(a.fecha);
          bv = parseFechaISO(b.fecha);
          return (av - bv) * dir;
        case 'total_neto':
          av = Number(a.total_neto || 0);
          bv = Number(b.total_neto || 0);
          return (av - bv) * dir;
        case 'numero_oc':
        default:
          av = Number(String(a.numero_oc || '').replace(/\D/g, '') || 0);
          bv = Number(String(b.numero_oc || '').replace(/\D/g, '') || 0);
          return (av - bv) * dir;
      }
    });
    return arr;
  }, [detalleOT, ocSortBy, ocSortDir, ocSearch]);

  const totalNetoFiltrado = useMemo(() => (
    ocsFiltradasOrdenadas.reduce((s, oc) => s + (Number(oc.total_neto) || 0), 0)
  ), [ocsFiltradasOrdenadas]);

  const renderSortIcon = (col) => {
    if (ocSortBy !== col) return <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-40" />;
    return ocSortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 inline ml-1 text-blue-700" />
      : <ArrowDown className="w-3 h-3 inline ml-1 text-blue-700" />;
  };

  // Iter 293.g: sort para la tabla de items del modal Detalle OC.
  const toggleItemSort = (col) => {
    if (itemSortBy === col) {
      setItemSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setItemSortBy(col);
      setItemSortDir(col === 'descripcion' ? 'asc' : 'desc');
    }
  };

  const renderItemSortIcon = (col) => {
    if (itemSortBy !== col) return <ArrowUpDown className="w-3 h-3 inline ml-1 opacity-40" />;
    return itemSortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 inline ml-1 text-blue-700" />
      : <ArrowDown className="w-3 h-3 inline ml-1 text-blue-700" />;
  };

  const itemsOrdenados = useMemo(() => {
    const rows = detalleOC?.detalles || [];
    if (itemSortBy === 'linea') {
      // Orden original (o inverso) sin re-ordenar por otro campo
      return itemSortDir === 'asc' ? rows : [...rows].reverse();
    }
    const dir = itemSortDir === 'asc' ? 1 : -1;
    const arr = [...rows];
    const totalLinea = (it) => Number(it.total_linea) || (Number(it.cantidad || 0) * Number(it.precio_unitario || it.valor_unitario || 0));
    arr.sort((a, b) => {
      let av; let bv;
      switch (itemSortBy) {
        case 'descripcion':
          av = String(a.descripcion || '').toLowerCase();
          bv = String(b.descripcion || '').toLowerCase();
          return av < bv ? -1 * dir : av > bv ? 1 * dir : 0;
        case 'cantidad':
          return (Number(a.cantidad || 0) - Number(b.cantidad || 0)) * dir;
        case 'precio':
          av = Number(a.precio_unitario || a.valor_unitario || 0);
          bv = Number(b.precio_unitario || b.valor_unitario || 0);
          return (av - bv) * dir;
        case 'total':
          return (totalLinea(a) - totalLinea(b)) * dir;
        default:
          return 0;
      }
    });
    return arr;
  }, [detalleOC, itemSortBy, itemSortDir]);

  // Iter 292.g (Carlos, feb-2026): cerrar modales con tecla Escape.
  // Prioridad: si hay modal de OC abierto, cierra ese; si no, cierra el de OT.
  useEffect(() => {
    if (!selectedOT && !selectedOC) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (selectedOC) {
        closeDetalleOC();
      } else if (selectedOT) {
        closeDetalle();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selectedOT, selectedOC]);

  // Formato para cantidades (conserva decimales si existen)
  const fmtCant = (num) => {
    if (num === null || num === undefined) return '0';
    return Number.isInteger(num) ? num.toLocaleString('es-CL') : num.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  // Formato para valores en $ (sin decimales)
  const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

  const handlePrint = () => {
    if (!filteredOTs.length) return;
    printTable({
      title: 'Resumen de Compras por OT',
      subtitle: `${filteredOTs.length} OTs en ejecución · ${searchOT ? `Filtro: "${searchOT}"` : 'Todas'} · ${new Date().toLocaleString('es-CL')}`,
      summary: data?.kpis ? [
        { label: 'OTs en ejecución', value: data.kpis.total_ots ?? filteredOTs.length },
        { label: 'OCs totales', value: data.kpis.total_ocs ?? 0 },
        { label: 'Monto total compras', value: `$${fmt(data.kpis.total_compras_neto)}`, color: '#1d4ed8', highlight: true },
        { label: 'Promedio por OT', value: `$${fmt(data.kpis.promedio_por_ot)}`, color: '#047857' },
      ] : [],
      columns: [
        { key: 'ot_numero', label: 'OT', mono: true, align: 'center' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'fecha_inicio', label: 'Inicio', format: (v) => v ? fmtFecha(v) : '-' },
        { key: 'cantidad_oc', label: '#OC', align: 'center' },
        { key: 'total_neto', label: 'Total Neto', align: 'right', mono: true, format: (v) => `$${fmt(v)}` },
        { key: 'estado', label: 'Estado', align: 'center', format: (v) => (v || '').toUpperCase() },
      ],
      rows: filteredOTs,
    });
  };

  const handleExcel = () => {
    if (!filteredOTs.length) return;
    exportToCSV({
      filename: 'resumen_compras_ots',
      title: 'CHIRINO STEEL · Resumen de Compras por OT',
      summary: data?.kpis ? [
        { label: 'OTs en ejecución', value: data.kpis.total_ots ?? filteredOTs.length },
        { label: 'OCs totales', value: data.kpis.total_ocs ?? 0 },
        { label: 'Total compras (CLP)', value: Math.round(data.kpis.total_compras_neto || 0) },
        { label: 'Promedio por OT (CLP)', value: Math.round(data.kpis.promedio_por_ot || 0) },
        { label: 'Búsqueda', value: searchOT || '-' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ] : [],
      columns: [
        { key: 'ot_numero', label: 'OT' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'cliente', label: 'Cliente' },
        { key: 'fecha_inicio', label: 'Fecha inicio', format: (v) => v ? fmtFecha(v) : '' },
        { key: 'fecha_termino', label: 'Fecha término', format: (v) => v ? fmtFecha(v) : '' },
        { key: 'cantidad_oc', label: 'Cantidad OCs', format: (v) => Number(v) || 0 },
        { key: 'total_neto', label: 'Total Neto (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'total_iva', label: 'IVA (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'total_bruto', label: 'Total Bruto (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'estado', label: 'Estado' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: filteredOTs,
    });
  };

  // Filtrar por búsqueda
  const filteredOTs = data?.ots_en_ejecucion?.filter(ot => 
    !searchOT || ot.ot_numero.toString().includes(searchOT) || 
    ot.descripcion?.toLowerCase().includes(searchOT.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">{error}</p>
        <Button onClick={loadData} className="mt-4">Reintentar</Button>
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="resumen-compras-ots-page">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 print:hidden">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Resumen de Compras - OTs en Ejecución
          </h1>
          <p className="text-sm text-muted-foreground">
            Costo de materiales por OT (OC Aprobadas) - <span className="text-green-600 font-medium">Valores Netos</span>
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handlePrint} variant="outline" size="sm">
            <Printer className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Print Header */}
      <div className="hidden print:block text-center mb-4 border-b-2 border-black pb-2">
        <h1 className="text-lg font-bold">RESUMEN COMPRAS - OTs EN EJECUCIÓN (Valores Netos)</h1>
        <p className="text-sm">{data?.total_ots} OTs | {data?.ots_con_costo} con costos</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">OTs en Ejecución</p>
          <p className="text-2xl font-bold text-blue-700">{data?.total_ots || 0}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-purple-700">OTs con Compras</p>
          <p className="text-2xl font-bold text-purple-700">{data?.ots_con_costo || 0}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-700">Total Costo Materiales (Neto)</p>
          <p className="text-2xl font-bold text-green-700">${fmt(data?.total_costo_neto)}</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 print:hidden">
        <Input
          placeholder="Buscar por N° OT o descripción..."
          value={searchOT}
          onChange={(e) => setSearchOT(e.target.value)}
          className="max-w-xs"
        />
      </div>

      {/* Modal Detalle OT (lista de OCs)
          Iter 292.f (Carlos, feb-2026): mientras el modal de OC esté abierto,
          ocultamos este modal para evitar doble overlay ("desenfocado") y que
          se pueda cerrar cómodamente. Al cerrar el modal de OC vuelve a
          verse el listado de OCs.
          Iter 292.g (Carlos, feb-2026): posicionar el modal desde el TOP (no
          centrado) para que el header y su botón de cierre siempre estén
          visibles aunque el contenido sea alto. Header sticky para que la X
          quede fija mientras haces scroll dentro del modal. */}
      {selectedOT && !selectedOC && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-6 px-4 print:hidden"
          data-testid="ot-detail-modal-resumen"
          onClick={(e) => { if (e.target === e.currentTarget) closeDetalle(); }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[calc(100vh-3rem)] flex flex-col">
            <div className="sticky top-0 z-10 flex justify-between items-center p-3 border-b bg-green-100 rounded-t-lg">
              <h3 className="font-bold text-sm">Detalle Costo Materiales - OT {selectedOT}</h3>
              <Button variant="ghost" size="sm" onClick={closeDetalle} data-testid="close-ot-detail-modal">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {loadingDetalle ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                </div>
              ) : detalleOT ? (
                <>
                  <div className="mb-4 p-3 bg-gray-50 rounded">
                    <p className="text-sm"><strong>OT:</strong> {detalleOT.ot_numero}</p>
                    <p className="text-sm"><strong>Descripción:</strong> {detalleOT.descripcion_ot || '-'}</p>
                    <p className="text-sm"><strong>Total Costo Neto:</strong> <span className="text-green-600 font-bold">${fmt(detalleOT.total_costo_neto)}</span></p>
                    <p className="text-sm"><strong>Cantidad OCs:</strong> {detalleOT.cantidad_ocs}</p>
                  </div>
                  
                  {detalleOT.detalle_ocs?.length > 0 ? (
                    <>
                      {/* Iter 293.f: buscador visible cuando hay más de 10 OCs */}
                      {detalleOT.detalle_ocs.length > 10 && (
                        <div className="mb-2 flex items-center gap-2">
                          <div className="relative flex-1 max-w-md">
                            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              type="text"
                              value={ocSearch}
                              onChange={(e) => setOcSearch(e.target.value)}
                              placeholder="Buscar por OC o Proveedor..."
                              className="pl-7 h-8 text-xs"
                              data-testid="ot-detail-oc-search"
                            />
                          </div>
                          <span className="text-[11px] text-muted-foreground" data-testid="ot-detail-oc-count">
                            {ocsFiltradasOrdenadas.length} / {detalleOT.detalle_ocs.length}
                          </span>
                        </div>
                      )}
                      <table className="w-full text-xs border-collapse">
                        <thead>
                          <tr className="bg-gray-200">
                            <th
                              className="border p-1 text-left cursor-pointer hover:bg-gray-300 select-none"
                              onClick={() => toggleOcSort('numero_oc')}
                              data-testid="ot-detail-th-oc"
                            >
                              N° OC{renderSortIcon('numero_oc')}
                            </th>
                            <th
                              className="border p-1 text-left cursor-pointer hover:bg-gray-300 select-none"
                              onClick={() => toggleOcSort('proveedor')}
                              data-testid="ot-detail-th-proveedor"
                            >
                              Proveedor{renderSortIcon('proveedor')}
                            </th>
                            <th
                              className="border p-1 text-center cursor-pointer hover:bg-gray-300 select-none"
                              onClick={() => toggleOcSort('fecha')}
                              data-testid="ot-detail-th-fecha"
                            >
                              Fecha{renderSortIcon('fecha')}
                            </th>
                            <th className="border p-1 text-center">Items</th>
                            <th
                              className="border p-1 text-right cursor-pointer hover:bg-gray-300 select-none"
                              onClick={() => toggleOcSort('total_neto')}
                              data-testid="ot-detail-th-total"
                            >
                              Total Neto{renderSortIcon('total_neto')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {ocsFiltradasOrdenadas.length > 0 ? ocsFiltradasOrdenadas.map((oc, idx) => (
                            <tr
                              key={`oc-${oc.numero_oc}`}
                              className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-100 cursor-pointer transition-colors`}
                              onClick={() => handleVerDetalleOC(oc.numero_oc)}
                              title="Click para ver detalle de la OC"
                            >
                              <td className="border p-1 font-mono text-blue-600 underline"><OCNumber oc={oc} size="sm" /></td>
                              <td className="border p-1 truncate max-w-[200px]" title={oc.proveedor}>{oc.proveedor}</td>
                              <td className="border p-1 text-center">{fmtFecha(oc.fecha)}</td>
                              <td className="border p-1 text-center">{oc.cantidad_items}</td>
                              <td className="border p-1 text-right font-mono text-green-600 font-medium">
                                ${fmt(oc.total_neto)}
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan={5} className="border p-3 text-center text-muted-foreground italic">
                                Sin resultados para &quot;{ocSearch}&quot;
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <tr className="bg-gray-800 text-white font-bold">
                            <td colSpan={4} className="border border-gray-600 p-1 text-right">
                              {ocSearch ? 'TOTAL FILTRADO:' : 'TOTAL:'}
                            </td>
                            <td className="border border-gray-600 p-1 text-right font-mono">
                              ${fmt(ocSearch ? totalNetoFiltrado : detalleOT.total_costo_neto)}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </>
                  ) : (
                    <p className="text-center text-muted-foreground py-4">
                      No hay órdenes de compra aprobadas para esta OT
                    </p>
                  )}
                </>
              ) : (
                <p className="text-center text-muted-foreground">No se encontró detalle</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle OC — Iter 292.g: alineación top + header sticky
          para garantizar que los botones Imprimir/X estén siempre visibles */}
      {selectedOC && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] flex items-start justify-center overflow-y-auto py-6 px-4 print:hidden"
          data-testid="oc-detail-modal-resumen"
          onClick={(e) => { if (e.target === e.currentTarget) closeDetalleOC(); }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-3rem)] flex flex-col">
            <div className="sticky top-0 z-10 flex justify-between items-center p-3 border-b bg-blue-100 rounded-t-lg">
              <h3 className="font-bold text-sm flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Detalle Orden de Compra N° {selectedOC}
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="w-4 h-4 mr-1" /> Imprimir
                </Button>
                <Button variant="ghost" size="sm" onClick={closeDetalleOC} data-testid="close-oc-detail-modal">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {loadingDetalleOC ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                  <p className="text-sm text-muted-foreground mt-2">Cargando detalle...</p>
                </div>
              ) : detalleOC ? (
                <>
                  {/* OC Header Info */}
                  <div className="mb-4 p-3 bg-gray-50 rounded border">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">N° OC</p>
                        <p className="font-bold text-blue-600">{detalleOC.orden_compra?.numero_oc}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">OT</p>
                        <p className="font-medium">{detalleOC.orden_compra?.ot_numero || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Fecha</p>
                        <p className="font-medium">{fmtFecha(detalleOC.orden_compra?.fecha)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Estado</p>
                        <p className={`font-medium ${detalleOC.orden_compra?.aprobada ? 'text-green-600' : 'text-orange-600'}`}>
                          {detalleOC.orden_compra?.aprobada ? 'Aprobada' : 'Pendiente'}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-muted-foreground">Proveedor</p>
                          <p className="font-medium">{detalleOC.orden_compra?.nombre_proveedor || detalleOC.orden_compra?.rut_proveedor || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">RUT Proveedor</p>
                          <p className="font-mono text-sm">{detalleOC.orden_compra?.rut_proveedor || '-'}</p>
                        </div>
                      </div>
                      {detalleOC.orden_compra?.solicitante && (
                        <div className="mt-2">
                          <p className="text-xs text-muted-foreground">Solicitante</p>
                          <p className="font-medium">{detalleOC.orden_compra.solicitante}</p>
                        </div>
                      )}
                      {detalleOC.orden_compra?.observaciones && (
                        <div className="mt-2">
                          <p className="text-xs text-muted-foreground">Observaciones</p>
                          <p className="text-sm text-gray-600">{detalleOC.orden_compra.observaciones}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Iter 292.f (Carlos, feb-2026) — Recepciones + Facturas asociadas.
                      Muestra número/fecha de recepción, guía (si existe), folio de factura
                      y su estado (pendiente/pagada/parcial/anulada por NC/sin datos). */}
                  {(detalleOC.recepciones?.length > 0 || detalleOC.facturas?.length > 0) && (
                    <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Recepciones */}
                      <div className="border rounded p-3 bg-emerald-50/40 border-emerald-200" data-testid="oc-detail-recepciones">
                        <div className="flex items-center gap-2 mb-2">
                          <Truck className="w-4 h-4 text-emerald-700" />
                          <h4 className="font-semibold text-sm text-emerald-900">Recepciones ({detalleOC.recepciones?.length || 0})</h4>
                        </div>
                        {detalleOC.recepciones?.length > 0 ? (
                          <div className="space-y-1.5">
                            {detalleOC.recepciones.map((r, i) => {
                              const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/compras/recepciones/${r.numero_recepcion}/pdf`;
                              return (
                              <div key={`rec-${r.numero_recepcion}-${i}`} className="text-xs bg-white rounded border border-emerald-100 px-2 py-1.5">
                                <div className="flex justify-between items-baseline">
                                  <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono font-semibold text-emerald-800 hover:text-emerald-950 hover:underline inline-flex items-center gap-1"
                                    title={`Ver PDF de Recepción ${r.numero_recepcion}`}
                                    data-testid={`rec-link-${r.numero_recepcion}`}
                                  >
                                    N° {r.numero_recepcion}
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                  <span className="text-emerald-700">{fmtFecha(r.fecha)}</span>
                                </div>
                                <div className="flex gap-2 flex-wrap mt-0.5 text-[11px]">
                                  {r.tipo_documento && (
                                    <span className="px-1.5 rounded bg-emerald-100 text-emerald-800">
                                      {r.tipo_documento === 'FA' ? 'Factura' : r.tipo_documento === 'GD' ? 'Guía Despacho' : r.tipo_documento}
                                    </span>
                                  )}
                                  {r.guia_despacho && (
                                    <span className="text-emerald-700">Guía: <strong>{r.guia_despacho}</strong></span>
                                  )}
                                  {r.responsable && (
                                    <span className="text-gray-600">· {r.responsable}</span>
                                  )}
                                  {typeof r.monto === 'number' && (
                                    <span className="ml-auto font-mono text-gray-700">${fmt(r.monto)}</span>
                                  )}
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 italic">Sin recepciones registradas</p>
                        )}
                      </div>

                      {/* Facturas cobradas */}
                      <div className="border rounded p-3 bg-amber-50/40 border-amber-200" data-testid="oc-detail-facturas">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-amber-700" />
                          <h4 className="font-semibold text-sm text-amber-900">Facturas ({detalleOC.facturas?.length || 0})</h4>
                        </div>
                        {detalleOC.facturas?.length > 0 ? (
                          <div className="space-y-1.5">
                            {detalleOC.facturas.map((f, i) => {
                              const estadoBadge = (() => {
                                switch (f.estado) {
                                  case 'pagada': return { bg: 'bg-green-100', text: 'text-green-800', label: 'Pagada' };
                                  case 'pendiente': return { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Pendiente de pago' };
                                  case 'parcial': return { bg: 'bg-yellow-100', text: 'text-yellow-900', label: 'Pago parcial' };
                                  case 'anulada_nc': return { bg: 'bg-red-100', text: 'text-red-800', label: 'Anulada por NC' };
                                  default: return { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Sin datos de pago' };
                                }
                              })();
                              return (
                                <div key={`fac-${f.folio}-${i}`} className="text-xs bg-white rounded border border-amber-100 px-2 py-1.5">
                                  <div className="flex justify-between items-baseline gap-2">
                                    <DocRefLink
                                      tipo="dte"
                                      folio={f.folio}
                                      rut={f.rut_emisor}
                                      label={`${f.tipo_doc_legible || 'Factura'} N° ${f.folio}`}
                                      icon={false}
                                      className="font-mono font-semibold !text-amber-800 hover:!text-amber-950"
                                      testId={`fac-link-${f.folio}`}
                                    />
                                    <span className={`px-1.5 rounded text-[10px] font-medium ${estadoBadge.bg} ${estadoBadge.text}`}>
                                      {estadoBadge.label}
                                    </span>
                                  </div>
                                  <div className="flex gap-2 flex-wrap mt-0.5 text-[11px] text-gray-700">
                                    {(f.fecha_emision || f.fecha_aceptacion || f.fecha_doc_recibido) && (
                                      <span>Emitida: {fmtFecha(f.fecha_emision || f.fecha_aceptacion || f.fecha_doc_recibido)}</span>
                                    )}
                                    {f.emisor_razon_social && (
                                      <span className="truncate max-w-[180px]" title={f.emisor_razon_social}>· {f.emisor_razon_social}</span>
                                    )}
                                    {typeof f.total === 'number' && (
                                      <span className="ml-auto font-mono">${fmt(f.total)}</span>
                                    )}
                                  </div>
                                  {f.fecha_pago_real && f.estado === 'pagada' && (
                                    <div className="text-[10px] text-green-700 mt-0.5">Pagada el {fmtFecha(f.fecha_pago_real)}</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 italic">Sin facturas asociadas</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* OC Items Table */}
                  {detalleOC.detalles?.length > 0 ? (
                    <>
                      <h4 className="font-medium text-sm mb-2">Detalle de Items ({detalleOC.cantidad_lineas || detalleOC.detalles.length})</h4>
                      <table className="w-full text-xs border-collapse mb-4">
                        <thead>
                          <tr className="bg-gray-200">
                            <th
                              className="border p-1 text-center w-12 cursor-pointer hover:bg-gray-300 select-none"
                              onClick={() => toggleItemSort('linea')}
                              data-testid="oc-detail-th-linea"
                            >
                              #{renderItemSortIcon('linea')}
                            </th>
                            <th
                              className="border p-1 text-left cursor-pointer hover:bg-gray-300 select-none"
                              onClick={() => toggleItemSort('descripcion')}
                              data-testid="oc-detail-th-descripcion"
                            >
                              Descripción{renderItemSortIcon('descripcion')}
                            </th>
                            <th
                              className="border p-1 text-center w-20 cursor-pointer hover:bg-gray-300 select-none"
                              onClick={() => toggleItemSort('cantidad')}
                              data-testid="oc-detail-th-cantidad"
                            >
                              Cantidad{renderItemSortIcon('cantidad')}
                            </th>
                            <th
                              className="border p-1 text-right w-24 cursor-pointer hover:bg-gray-300 select-none"
                              onClick={() => toggleItemSort('precio')}
                              data-testid="oc-detail-th-precio"
                            >
                              V. Unitario{renderItemSortIcon('precio')}
                            </th>
                            <th
                              className="border p-1 text-right w-24 cursor-pointer hover:bg-gray-300 select-none"
                              onClick={() => toggleItemSort('total')}
                              data-testid="oc-detail-th-total"
                            >
                              Total{renderItemSortIcon('total')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemsOrdenados.map((item, idx) => (
                            <tr key={`det-${item.codigo || idx}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="border p-1 text-center">{idx + 1}</td>
                              <td className="border p-1" title={item.descripcion}>
                                {item.descripcion || '-'}
                              </td>
                              <td className="border p-1 text-center">{fmtCant(item.cantidad)}</td>
                              <td className="border p-1 text-right font-mono">${fmt(item.precio_unitario || item.valor_unitario)}</td>
                              <td className="border p-1 text-right font-mono">${fmt(item.total_linea || (item.cantidad * (item.precio_unitario || item.valor_unitario)))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No hay items detallados para esta OC
                    </p>
                  )}

                  {/* OC Totals */}
                  <div className="bg-gray-100 rounded p-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Total Neto:</span>
                      <span className="text-lg font-bold text-green-600">${fmt(detalleOC.orden_compra?.neto || detalleOC.orden_compra?.total)}</span>
                    </div>
                    {detalleOC.orden_compra?.total && detalleOC.orden_compra?.neto && detalleOC.orden_compra?.total !== detalleOC.orden_compra?.neto && (
                      <div className="flex justify-between items-center mt-1 text-sm text-muted-foreground">
                        <span>Total c/IVA:</span>
                        <span>${fmt(detalleOC.orden_compra?.total)}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-muted-foreground">No se encontró detalle para esta OC</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {filteredOTs.length > 0 ? (
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-800 text-white">
              <th className="border border-gray-600 p-1 text-left w-16">OT</th>
              <th className="border border-gray-600 p-1 text-left">Descripción</th>
              <th className="border border-gray-600 p-1 text-center w-16">OCs</th>
              <th className="border border-gray-600 p-1 text-right w-28">Costo Neto</th>
              <th className="border border-gray-600 p-1 text-center w-16 print:hidden">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {filteredOTs.map((ot, idx) => (
              <tr key={`ot-${ot.ot_numero}`} className={ot.costo_materiales_neto > 0 ? 'bg-green-50' : (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
                <td className="border p-1 font-mono font-medium">{ot.ot_numero}</td>
                <td className="border p-1 truncate max-w-[300px]">{ot.descripcion || '-'}</td>
                <td className="border p-1 text-center">{ot.cantidad_ocs || 0}</td>
                <td className="border p-1 text-right font-mono text-green-600 font-medium">
                  {ot.costo_materiales_neto > 0 ? `$${fmt(ot.costo_materiales_neto)}` : '-'}
                </td>
                <td className="border p-1 text-center print:hidden">
                  {ot.costo_materiales_neto > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0"
                      onClick={() => handleVerDetalle(ot.ot_numero)}
                    >
                      <Eye className="w-3 h-3" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-800 text-white font-bold">
              <td colSpan={3} className="border border-gray-600 p-1 text-right">TOTAL GENERAL:</td>
              <td className="border border-gray-600 p-1 text-right font-mono">${fmt(data?.total_costo_neto)}</td>
              <td className="border border-gray-600 p-1 print:hidden"></td>
            </tr>
          </tfoot>
        </table>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-muted-foreground">No hay OTs en ejecución con datos de compras</p>
          <p className="text-sm text-muted-foreground mt-1">Importe los datos desde Access</p>
        </div>
      )}
    </div>
  );
};
